import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {EnterprisePortalApi, Mail, MailFolderEnum, WebmailApi} from "components/src/api/portal/generated";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {PageTitle} from "components/src/common/PageTitle";
import {ReloadButton} from "components/src/common/ReloadButton";
import {RenderAlert} from "components/src/common/RenderAlert";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {DateUtils} from "components/src/utils/DateUtils";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {
    Eye as ReadIcon,
    EyeClosed as UnreadIcon,
    Mail as MailIcon,
    Plus as NewMailIcon,
    Xmark as DeleteIcon
} from "iconoir-react";
import {useEffect, useState} from "react";
import {Navbar} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {CreateNewMailPopup} from "./CreateNewMailPopup";
import {ViewMailPopup} from "./ViewMailPopup";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());
const enterprisePortalApi = new EnterprisePortalApi(ApiConfig.config());

export const Messages = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The selected folder
     */
    const [folder, setFolder] = useState<MailFolderEnum>(MailFolderEnum.Inbox);
    /**
     * The number of available messages
     */
    const [messageCount, setMessageCount] = useState<number>();
    /**
     * The loaded messages
     */
    const [messages, setMessages] = useState<Mail[]>([]);
    /**
     * The messages for which the checkbox is selected
     */
    const [selectedMessages, setSelectedMessages] = useState<Array<Mail>>([]);
    /**
     * The currently selected messages
     */
    const [currentMessage, setCurrentMessage] = useState<Mail | undefined>(undefined);
    /**
     * If true, the "confirm delete selected" modal dialog will be shown
     */
    const [showDeleteConfirmSelected, setShowDeleteConfirmSelected] = useState<boolean>(false);
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The number of messages to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * If true, the licenses will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * If true, the reply popup will be shown
     */
    const [showViewMailPopup, setShowViewMailPopup] = useState<boolean>(false);
    /**
     * If true, the new mail popup will be shown
     */
    const [showNewMailPopup, setShowNewMailPopup] = useState<boolean>(false);
    /**
     * The used quota
     */
    const [quotaUsage, setQuotaUsage] = useState<number>();
    /**
     * The quota limit
     */
    const [quotaLimit, setQuotaLimit] = useState<number>();
    /**
     * Reload quota is on state change
     */
    const [quotaState, setQuotaState] = useState<number>(0);
    /**
     * true if new mail can be created
     */
    const [isCreateNewMailAllowed, setIsCreateNewMailAllowed] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset the state when we load new data
        setForceReload(false);

        webmailApi.webmailPortalControllerGetMessageCount(folder)
            .then(response => {
                setMessageCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t,
        folder]);

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset the state when we load new data
        setForceReload(false);

        webmailApi.webmailPortalControllerGetMessages(folder, (currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response =>
            {
                const loadedMessages : Mail[] = response.data;

                setMessages(loadedMessages);

                if (loadedMessages.length > 0) {
                    setCurrentMessage(loadedMessages[0]);
                }
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t,
        folder,
        currentPage,
        rowsPerPage]);

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerGetQuota(folder)
            .then((response) =>
            {
                // assume for now that we only have one quota
                response.data[0].resource.forEach(resource => {
                    setQuotaUsage(resource.usage * 1024);
                    setQuotaLimit(resource.limit * 1024);
                })
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [folder,
        navigate,
        setSpinnerStateCounter,
        quotaState,
        t]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        enterprisePortalApi.enterprisePortalControllerGetEnterprisePortalProperties()
            .then(response => {
                setIsCreateNewMailAllowed(response.data.webmailIsCreateNewMailAllowed);
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t]);

    const deleteMessages = (messages : Array<Mail>) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerDeleteMessages(folder, messages.map(message => message.uid))
            .then(() => {
                reload();
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirmSelected(false);
                setSelectedMessages([]);
            })
    }

    /**
     * Called when the folder is changed
     */
    const onFolderChanged = (folder : MailFolderEnum) =>
    {
        setFolder(folder)
        // reset state
        setCurrentPage(1);
        setSelectedMessages([])
        setCurrentMessage(undefined);
        reload();
    }

    const onChangeSelect = (checked : boolean, selectedMessage : Mail) =>
    {
        setSelectedMessages(checked ?
            [...selectedMessages, selectedMessage] :
            selectedMessages.filter(message => message.uid !== selectedMessage.uid)
        );
    }

    const invertSelection = () => {
        setSelectedMessages([...messages].filter(message => !selectedMessages.includes(message)));
    }

    const setReadStatus = (read : boolean) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        webmailApi.webmailPortalControllerSetReadFlag(folder, read, selectedMessages.map(message => message.uid))
            .then(() => {
                reload();
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setSelectedMessages([]);
            })
    }

    const seenCssClass = (message : Mail) =>
    {
        if (!message.seen) {
            return "tr-font-weight-bold";
        }

        return null;
    }

    const clickedCssClass = (message : Mail) =>
    {
        if (message.uid === currentMessage?.uid) {
            return "tr-secondary";
        }

        return null;
    }

    const onMessageClicked = (message : Mail) => {
        setCurrentMessage(message);
        setShowViewMailPopup(true);
    }

    const newMail = () => {
        setShowNewMailPopup(true);
    }

    /**
     * Is called when the new mail popup is closed
     */
    const onNewMailPopupClose = () => {
        setShowNewMailPopup(false);
    }

    /**
     * Is called when the view mail popup is closed
     */
    const onViewMailPopupClose = (dialogCloseCause : DialogCloseCause) =>
    {
        setShowViewMailPopup(false);

        if (dialogCloseCause === DialogCloseCause.Succes) {
            reload();
        }
    }

    function reload() {
        setForceReload(true);
        setQuotaState(quotaState + 1);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("portal.messages.confirm-delete-selected-messages")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={() => deleteMessages(selectedMessages)}
                           showConfirm={showDeleteConfirmSelected}
                           setShowConfirm={setShowDeleteConfirmSelected}
            />

            {showNewMailPopup &&
                <CreateNewMailPopup onClose={onNewMailPopupClose}/>}

            {showViewMailPopup && currentMessage != undefined &&
                <ViewMailPopup mail={currentMessage} onClose={onViewMailPopupClose}/>}

            <Row>
                <Col>
                    <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                        <div className="d-flex justify-content-start">
                            <PageTitle title={t("portal.messages.title")} icon={<MailIcon />}/>
                        </div>
                        <div className="d-flex flex-fill align-items-center justify-content-end">
                            <div className="text-muted d-none d-md-flex">
                                {t("portal.messages.disk-usage")}: {Utils.formatBytes(quotaUsage)} / {Utils.formatBytes(quotaLimit)}
                                <span className="ms-2">
                                ({quotaUsage !== undefined && quotaLimit !== undefined && Utils.percentageFormatter(100 * quotaUsage / quotaLimit)})
                                </span>
                            </div>

                            <ReloadButton onClickReload={() => reload()} />
                        </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <Col>
                    <div className="d-flex flex-row align-items-center mb-4">
                        <div className="d-flex justify-content-start">
                            <Form.Select aria-label={t("portal.messages.folder")}
                                         value={folder}
                                         onChange={(e) => onFolderChanged(Utils.stringToEnum(MailFolderEnum, e.target.value as MailFolderEnum))}>
                                <option value="INBOX">{t("portal.messages.folder-inbox")}</option>
                                <option value="SENT">{t("portal.messages.folder-sent")}</option>
                            </Form.Select>
                        </div>
                        <div className="d-flex flex-fill justify-content-end align-items-center">
                            <Button variant="success"
                                    onClick={() => newMail()}
                                    disabled={!isCreateNewMailAllowed}>
                                <NewMailIcon height="1em" width="1em"/> {t("portal.messages.new-mail")}
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover size={"sm"}>
                        <thead className="sticky-top">
                        <tr className="text-nowrap">
                            {selectedMessages.length === 0 ?
                                <>
                                    <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                    <th>{t("portal.messages.subject")} </th>
                                    {folder === MailFolderEnum.Inbox &&
                                        <th>{t("portal.messages.from")} </th>
                                    }
                                    {folder === MailFolderEnum.Sent &&
                                        <th>{t("portal.messages.to")} </th>
                                    }
                                    <th>{t("portal.messages.date")} </th>
                                    <th>{t("portal.messages.id")} </th>
                                </>
                                :
                                <th colSpan={5}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedMessages([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedMessages.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link disabled={selectedMessages.length === 0}
                                                      onClick={() => setShowDeleteConfirmSelected(true)}>
                                                <DeleteIcon className="text-danger"/> {t("portal.messages.delete-selected")}
                                            </Nav.Link>

                                            <Nav.Link disabled={selectedMessages.length === 0}
                                                      onClick={() => setReadStatus(true)}>
                                                <ReadIcon className="text-success"/> {t("portal.messages.mark-as-read")}
                                            </Nav.Link>

                                            <Nav.Link disabled={selectedMessages.length === 0}
                                                      onClick={() => setReadStatus(false)}>
                                                <UnreadIcon className="text-success"/> {t("portal.messages.mark-as-unread")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            }

                        </tr>
                        </thead>

                        <tbody>
                            {messages.map((message: Mail) => (
                                // make the tr focusable and execute on enter key
                                <tr key={message.uid}
                                        className={`text-nowrap ${seenCssClass(message)} ${clickedCssClass(message)}`}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => Utils.clickOnEnterKeyPressed(e, () => onMessageClicked(message))}>

                                    <td className="td-w-min">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedMessages.includes(message)}
                                            onChange={(e) => onChangeSelect(e.target.checked, message)}
                                            inline
                                        />
                                    </td>

                                    <td onClick={() => onMessageClicked(message)}>
                                        <Truncate value={message.subject} maxLength={120}/>
                                    </td>

                                    {folder === MailFolderEnum.Inbox &&
                                        <td onClick={() => onMessageClicked(message)}>
                                            <Truncate value={StringUtils.unquoteEmail(message.from)} maxLength={60}/>
                                        </td>
                                    }

                                    {folder === MailFolderEnum.Sent &&
                                        <td onClick={() => onMessageClicked(message)}>
                                            <Truncate value={message.to?.map(e => StringUtils.unquoteEmail(e)).join(", ")} maxLength={60}/>
                                        </td>
                                    }

                                    <td onClick={() => onMessageClicked(message)}>{DateUtils.fromTimestampToLocaleDateStringShort(message.date)}</td>
                                    <td onClick={() => onMessageClicked(message)}>{message.uid}</td>
                                </tr>
                            ))}
                            </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-2 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {messageCount} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage} maxRowsPerPage={40}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={messageCount ?? 0}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
